import raw from './tools.json';

export interface RouteFaq {
  q: string;
  a: string;
}

export interface RouteExample {
  input: string;
  output: string;
  note: string;
}

export interface EndpointPattern {
  path: string;
  title: string;
  description: string;
  robots: string;
}

export interface SiteMeta {
  name: string;
  origin: string;
  defaultImage: string;
  /** Intrinsic size of defaultImage, mirrored in the og:image:* meta tags. */
  imageWidth: number;
  imageHeight: number;
  logo: string;
  twitterCard: string;
  locale: string;
  endpointPattern: EndpointPattern;
}

export interface RouteMeta {
  slug: string;
  path: string;
  navLabel: string;
  /** false for client-only routes that must be served the noindex SPA fallback. */
  prerender: boolean;
  inSitemap: boolean;
  changefreq?: string;
  priority?: string;
  title: string;
  description: string;
  keywords?: string;
  h1: string;
  tagline: string;
  shortDescription: string;
  intro: string[];
  whenToUse: string[];
  example: RouteExample;
  faq: RouteFaq[];
  related: string[];
}

const data = raw as unknown as { site: SiteMeta; routes: RouteMeta[] };

/** Site-wide metadata used by the SEO component and the prerender script. */
export const SITE: SiteMeta = data.site;

/** Every route, in the order they should appear in navigation and the sitemap. */
export const ROUTES: RouteMeta[] = data.routes;

/** The tool pages only (everything that is not the homepage or the tools index). */
export const TOOLS: RouteMeta[] = ROUTES.filter(
  (route) => route.slug !== 'home' && route.slug !== 'tools'
);

const ROUTES_BY_SLUG: Record<string, RouteMeta> = ROUTES.reduce(
  (acc, route) => ({ ...acc, [route.slug]: route }),
  {}
);

export function routeBySlug(slug: string): RouteMeta | undefined {
  return ROUTES_BY_SLUG[slug];
}

/** Same as routeBySlug, but throws instead of returning undefined for a typo'd slug. */
export function requireRoute(slug: string): RouteMeta {
  const route = ROUTES_BY_SLUG[slug];
  if (!route) {
    throw new Error(`Unknown route slug: ${slug}`);
  }
  return route;
}

export const HOME_ROUTE = requireRoute('home');
export const TOOLS_ROUTE = requireRoute('tools');
export const BASE64_ROUTE = requireRoute('base64');
export const URL_ENCODING_ROUTE = requireRoute('url-encoding');
export const XML_ENCODING_ROUTE = requireRoute('xml-encoding');
export const JSON_YAML_ROUTE = requireRoute('json-yaml');
export const HASH_ROUTE = requireRoute('hash');
export const JWT_ROUTE = requireRoute('jwt');
export const UUID_ROUTE = requireRoute('uuid');
export const TIMESTAMP_ROUTE = requireRoute('timestamp');
export const REGEX_ROUTE = requireRoute('regex');

/** Related routes of a page, skipping any slug that does not exist. */
export function relatedRoutes(route: RouteMeta): RouteMeta[] {
  return route.related
    .map((slug) => ROUTES_BY_SLUG[slug])
    .filter((related): related is RouteMeta => Boolean(related));
}

/** Absolute, canonical URL for a site path. */
export function absoluteUrl(path: string): string {
  return `${SITE.origin}${path === '/' ? '/' : path}`;
}
